/**
 * Claim-workflow mapping hook surface - stub.
 *
 * Phase 1 of the tenant-agnostic claims migration removed the
 * ClaimWorkflowMapping table. The generic apps.workflows engine
 * resolves the active workflow per tenant directly (one
 * resource_type='claim' workflow per tenant); per-product /
 * per-category / per-template / per-warranty-template scoped routing
 * is queued as a future enhancement on the generic engine
 * (entity_type binding + workflow condition predicates).
 *
 * Until then this hook returns an empty mapping list so the legacy
 * Claim Mappings admin page renders an empty state without throwing.
 * Create/delete mutations throw a descriptive error so any UI that
 * triggers them surfaces the regression.
 */

import { useMutation, useQuery } from '@tanstack/react-query'

export type ClaimMappingLevel =
  | 'GLOBAL'
  | 'TEMPLATE'
  | 'CATEGORY'
  | 'PRODUCT'
  | 'WARRANTY_TEMPLATE'

export interface ClaimWorkflowMapping {
  uuid: string
  level: ClaimMappingLevel
  product_uuid: string | null
  product_name: string | null
  category_uuid: string | null
  category_name: string | null
  product_template_uuid: string | null
  product_template_name: string | null
  warranty_template_uuid: string | null
  warranty_template_name: string | null
  claim_workflow_uuid: string
  claim_workflow_name: string
  priority: number
  is_active: boolean
  created_at: string
  updated_at: string
}

const MAPPING_UNAVAILABLE =
  'Per-product workflow mappings are temporarily unavailable. ' +
  'All claims for this organisation use the active claim workflow ' +
  'configured on the Workflows tab. Scoped routing returns when the ' +
  'generic apps.workflows engine ships entity-type binding (tracked ' +
  'in docs/plan-claims-tenant-agnostic-migration.md).'

export function useClaimWorkflowMappings() {
  return useQuery<ClaimWorkflowMapping[]>({
    queryKey: ['claim-workflow-mappings'],
    queryFn: async () => [],
  })
}

export function useCreateClaimWorkflowMapping() {
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      void data
      throw new Error(MAPPING_UNAVAILABLE)
    },
  })
}

export function useDeleteClaimWorkflowMapping() {
  return useMutation({
    mutationFn: async (uuid: string) => {
      void uuid
      throw new Error(MAPPING_UNAVAILABLE)
    },
  })
}
